





























import { defineComponent, ref, useContext, reactive, watch, onUpdated } from '@nuxtjs/composition-api'
import axios from 'axios'

// Composables
import useProductAnalytics from '@/composables/analytics/useProductAnalytics'

// Components
import ImageEnhancementSidebar from '~/components/feature/image-enhancement/Sidebar.vue'
import ImageEnhancementViewer from '~/components/feature/image-enhancement/Viewer.vue'

// Common
import { getDisplayNameForApplicationCode } from '~/common/utils/parsing'

// Types
import { ImageEnhancementConfigInterface } from '~/types/image-enhancement/imageEnhancementTypes'
import { Figure, MappedProduct } from '~/types/product/productTypes'

export default defineComponent({
  name: 'ImageEnhancementModal',

  components: {
    ImageEnhancementViewer,
    ImageEnhancementSidebar,
  },

  props: {
    applicationGroup: {
      type: String,
      required: false,
      default: 'all',
    },
    productData: {
      type: Object as () => MappedProduct,
      required: true,
    },
    index: {
      type: String,
      required: true,
    },
    type: {
      type: String as () => ImageEnhancementConfigInterface['type'],
      required: false,
      default: 'pdp',
    },
    targetName: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup(props) {
    const ctx = useContext()
    const { trackImageEnhancement } = useProductAnalytics()
    const ImageEnhancementViewer = ref<any>(null)
    const ImageEnhancementSidebar = ref<any>(null)
    const loading = ref<boolean>(true)

    const checkGroupOrCodeInput = (input: string | undefined) => {
      if (input === 'all') {
        return 'all'
      }
      if (input === 'additional data') {
        return 'additional data'
      }
      return null
    }

    const getApplicationGroup = (applicationCode: string | undefined) => {
      if (checkGroupOrCodeInput(applicationCode)) {
        return applicationCode
      }

      // Check if we can find the application and the group. If not, return the input of this method
      return (
        config.productdata?.attributes?.lots?.lot.applications?.application.find(
          (application) => application.code === applicationCode
        )?.group || applicationCode
      )
    }

    const getApplicationCode = (applicationGroup: string): string => {
      if (checkGroupOrCodeInput(applicationGroup)) {
        return applicationGroup
      }

      // Check if we can find the application and the group. If not, return the input of this method
      return (
        config.productdata?.attributes?.lots?.lot.applications?.application.find(
          (application) => application.group === applicationGroup
        )?.code || applicationGroup
      )
    }

    const config = reactive<ImageEnhancementConfigInterface>({
      productdata: props.productData,
      application: {
        activeApplication: '',
        selectedApplication: null,
        groupsAndCodes: {},
      },
      selectedProtocolId: null,
      selectedProtocol: '',
      currentSlide: parseInt(props.index) - 1,
      images: {
        cloudflareImages: null,
        zoomImagesArray: [],
        nonOptimizedImageWidth: 520,
        zoomImage2XWidth: 1040,
        zoomImage4XWidth: 2080,
      },
      zoomEnabled: false,
      showZoom: false,
      showNav: true,
      showPrev: true,
      showNext: true,
      carouselKey: 0,
      sidebarOpen: false,
      showImageEnhancement: true,
      type: props.type,
      targetName: props.targetName,
      colorMode: 'light',
    })

    watch(
      props,
      async () => {
        config.productdata = props.productData
        config.showImageEnhancement = true
        config.targetName = props.targetName

        if (process.client) {
          // First get the Cloudflare images with plugin client
          config.images.cloudflareImages = await ctx.$cloudflare.getCloudflareImagesByID(props.productData.key)

          // Check if we have applications
          if (!config.productdata?.attributes?.lots?.lot.applications?.application) {
            config.application.activeApplication = 'additional data'
          } else {
            config.application.activeApplication = getApplicationCode(props.applicationGroup)
            // Now set selected app and protocol
            setSelectedApplication()
            setSelectedProtocol()
          }

          // Create an object with all application groups and codes
          getApplicationGroupsAndCodes()

          // Create the zoomArray and set the current slide
          calculateImageSizes()
          calculateShowNav()

          // Hide loader
          loading.value = false

          // Track opening image enhancement modal
          trackImageEnhancement({
            openFromPanelBuilder: props.type === 'signalstar',
            enlargeEnabled: undefined,
            switchApplication: undefined,
          })
        }
      },
      { immediate: true }
    )

    onUpdated(() => {
      if (ImageEnhancementViewer.value && config.currentSlide !== 0) {
        ImageEnhancementViewer.value.imageEnhancementImageSlider.imageEnhancementCaroussel.goTo(config.currentSlide)
      }
    })

    const calculateShowNav = () => {
      config.showNav = config.images.zoomImagesArray.length !== 1
      config.showPrev = config.currentSlide !== 0
      config.showNext = config.currentSlide + 1 !== config.images.zoomImagesArray.length
    }

    // get images from input
    const getAllImages = (input: Figure[] | undefined, protocol?: string, applicationGroup: string = '') => {
      if (!input) return

      input.forEach((figure, index: number) => {
        imageSizingLoop(figure, index, protocol, applicationGroup)
      })
    }

    const calculateImageSizes = () => {
      config.images.zoomImagesArray = []

      // check application images
      if (config.application.activeApplication !== 'additional data') {
        config.application.selectedApplication?.forEach((application) => {
          const applicationGroup = getApplicationGroup(application.code)

          if (application.figures) {
            // If is all, check the protocol number.
            // If it is not set, find the group based on the code and see if the
            // other group has a protocol number set
            if (config.application.activeApplication === 'all') {
              let protocolId = application.protocol?.number || undefined

              if (!protocolId) {
                protocolId = config.application
                  .selectedApplication!.filter((application) => application.group === applicationGroup)
                  .map((application) => application.protocol?.number)[0]
              }
            }

            // Fetch protocolId and see if we need to get it from a different application
            // in the same group
            getAllImages(application.figures?.figure, application.protocol?.number, applicationGroup)
          }
        })
      }

      // Check root level images if application is all
      if (
        config.application.activeApplication === 'all' ||
        config.application.activeApplication === 'additional data'
      ) {
        if (config.productdata?.attributes.figures?.figure) {
          getAllImages(config.productdata?.attributes.figures?.figure)
        }
      }

      // If zoomImagesArray length is lower then the currentSlide, set currentSlide to length
      if (config.images.zoomImagesArray.length < config.currentSlide) {
        config.currentSlide = config.images.zoomImagesArray.length - 1
      }
    }

    const imageSizingLoop = (figure: Figure, index: number, protocol?: string, applicationGroup: string = '') => {
      // Exclude image without a filename
      if (!figure.filename) return

      const protocolId = protocol ?? null
      const url = ctx.$img(`/product/image/${figure.filename}`, null, { provider: 'ms' })

      const foundImageData = config.images.cloudflareImages
        ? config.images.cloudflareImages?.figures?.filter((image) => {
            if (image.isProtected === 'true') {
              return false
            }

            return image['dam-media-id'] === figure['dam-media-id']!
          })
        : null

      let variants = null

      if (foundImageData && foundImageData[0]) {
        variants = foundImageData[0].variants
      }

      if (parseInt(figure.imagewidth) >= config.images.zoomImage2XWidth) {
        const scale = parseInt(figure.imagewidth) >= config.images.zoomImage4XWidth ? 4 : 2
        config.images.zoomImagesArray.push({
          index,
          scale,
          url,
          caption: (() => {
            return figure.caption ? figure.caption : ''
          })(),
          protocolId,
          variants,
          width: parseInt(figure.imagewidth),
          applicationGroup,
        })

        // Show zoom if selected group is IHC or IF
        // Check if the selected application is an array.
        // It can be a string for 'all' and 'additinal data' for images without application
        config.showZoom =
          Array.isArray(config.application.selectedApplication) &&
          (config.application.selectedApplication![0].group === 'IHC' ||
            config.application.selectedApplication![0].group === 'IF')
      } else {
        config.images.zoomImagesArray.push({
          index,
          scale: 1,
          url,
          caption: (() => {
            return figure.caption ? figure.caption : ''
          })(),
          protocolId,
          variants,
          width: parseInt(figure.imagewidth),
          applicationGroup,
        })
      }
    }

    const setSelectedApplication = () => {
      const applications = config.productdata?.attributes?.lots?.lot.applications?.application

      if (!applications) {
        return
      }

      const filteredApplication = applications.filter((el) => el.code === config.application.activeApplication)

      if (filteredApplication.length > 0) {
        config.application.selectedApplication = filteredApplication
      } else {
        config.application.selectedApplication = applications
        config.application.activeApplication = 'all'
      }
    }

    const getProtocolId = (): string | null => {
      if (config.application.activeApplication === 'additional data') {
        return null
      }

      // If no application because this product does not have one, return null
      if (!config.application.selectedApplication) {
        return null
      }

      // On load, show the first protocol
      if (config.application.activeApplication === 'all' && config.currentSlide === 0) {
        return config.application.selectedApplication![0]?.protocol?.number ?? null
      }

      let protocolId = null

      // Check for protocol in activeAplpication code
      protocolId = config.productdata?.attributes?.lots?.lot.applications?.application.find(
        (application) => application.code === config.application.activeApplication
      )?.protocol?.number

      return protocolId || null
    }

    const getApplicationGroupsAndCodes = () => {
      // Stop when there's nothing to map
      if (!config.productdata?.attributes?.lots?.lot.applications?.application) return {}

      // Get applications
      config.application.groupsAndCodes = config.productdata.attributes?.lots.lot.applications.application.reduce(
        (acc: Record<string, Array<{ code: string; method: string }>>, application) => {
          // If this application has no images, continue
          if (!application.figures || application.figures.figure.length === 0) return acc

          // Check if this is a group that needs some special mapping
          const applicationGroupName = getDisplayNameForApplicationCode(application.group)

          if (applicationGroupName && application.code && application.method) {
            // If group is not defined, add to the array
            if (!acc[applicationGroupName]) {
              acc[applicationGroupName] = [{ code: application.code, method: application.method }]
            } else {
              // If group is defined, add the code to the codes array
              acc[applicationGroupName].push({ code: application.code, method: application.method })
            }
          }

          return acc
        },
        {}
      )
    }

    const setSelectedProtocol = async (protocolNumber?: string | null) => {
      // add protocol ID when applicatioon == all
      const protocolUrl = `${ctx.$config.cst.mediaUrl}/json2/protocol/`
      const protocolId = protocolNumber || getProtocolId()

      // When no protolId, return and do not fetch
      if (!protocolId) {
        config.sidebarOpen = false
        config.selectedProtocol = ''
        config.selectedProtocolId = null
        return
      }

      // If the protocolId is the same as the current one, return
      if (config.selectedProtocolId === protocolId) {
        return
      }

      // set the id in the config to check wheter we need to fetch a new protocol on slide change for all applications
      config.selectedProtocolId = protocolId

      try {
        const { data } = await axios({
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          url: ctx.$config.cst.isPreview
            ? `${protocolUrl}${protocolId}?env=${ctx.$config.cst.featureEnvironment}&wip=true`
            : `${protocolUrl}${protocolId}`,
        })

        if (data['step-protocol'].markup) {
          config.selectedProtocol = data['step-protocol'].markup
        } else {
          config.selectedProtocolId = null
        }
      } catch (e) {
        console.error(e)
      }
    }

    // Listen to emits and navigate
    const changeApplication = (applicationCode: string) => {
      // Track switching application
      trackImageEnhancement({ switchApplication: applicationCode })

      // Set new active application
      config.application.activeApplication = applicationCode
      config.selectedProtocolId = null

      // Set new selected application
      if (applicationCode !== 'additional data') {
        setSelectedApplication()
      }

      setSelectedProtocol()

      // re-init caroussel and reset currentslide
      config.carouselKey++
      config.currentSlide = 0
      config.images.zoomImagesArray = []
      config.zoomEnabled = false
      config.showZoom = false

      // check sizes new images in array
      calculateImageSizes()

      // Check if we need to show the nav in the footer
      calculateShowNav()
    }

    const changeCurrentSlide = (value: number) => {
      if (ImageEnhancementViewer.value) {
        ImageEnhancementViewer.value.imageEnhancementImageSlider.imageEnhancementCaroussel.goTo(value)
      }
    }

    const onPrevClick = () => {
      if (ImageEnhancementViewer.value) {
        ImageEnhancementViewer.value.imageEnhancementImageSlider.imageEnhancementCaroussel.prev()
      }
    }
    const onNextClick = () => {
      if (ImageEnhancementViewer.value) {
        ImageEnhancementViewer.value.imageEnhancementImageSlider.imageEnhancementCaroussel.next()
      }
    }

    const onZoomButtonClick = () => {
      config.zoomEnabled = !config.zoomEnabled

      // If true, track pushing on the enlarge button
      if (config.zoomEnabled) {
        trackImageEnhancement({ enlargeEnabled: true })
      }
    }

    const onSliderChange = (index: number) => {
      config.currentSlide = index
      config.zoomEnabled = false

      setSelectedProtocol(config.images.zoomImagesArray[index].protocolId)
      calculateShowNav()

      // console.log(config.images.zoomImagesArray[index].width >= config.images.zoomImage2XWidth)
      // Calculate if we need to enable zoom or not
      if (
        (config.images.zoomImagesArray[index].applicationGroup === 'IHC' ||
          config.images.zoomImagesArray[index].applicationGroup === 'IF') &&
        config.images.zoomImagesArray[index].width >= config.images.zoomImage2XWidth
      ) {
        config.showZoom = true
      } else {
        config.zoomEnabled = false
        config.showZoom = false
      }
    }

    const onSetSidebarOpen = (value: boolean) => {
      config.sidebarOpen = value
    }

    const onCloseModal = () => {
      config.showImageEnhancement = false

      // reset all values
      config.productdata = null
      config.application.activeApplication = 'all'
      config.application.selectedApplication = null
      config.selectedProtocolId = null
      config.selectedProtocol = ''
      config.currentSlide = 0
      config.images.zoomImagesArray = []
      config.zoomEnabled = false
      config.showZoom = false
      config.showNav = true
      config.showPrev = true
      config.showNext = true
      config.carouselKey = 0
      config.sidebarOpen = false

      // reset some nested values
      ImageEnhancementSidebar.value._data.selectedIcon = ''
      ImageEnhancementViewer.value.$children[0]._data.selectedApplicationCode = 'all'
    }

    const onColorModeChange = (value: boolean) => {
      config.colorMode = value ? 'dark' : 'light'
    }

    return {
      ImageEnhancementViewer,
      ImageEnhancementSidebar,
      loading,
      changeApplication,
      changeCurrentSlide,
      onPrevClick,
      onNextClick,
      onZoomButtonClick,
      onSliderChange,
      onSetSidebarOpen,
      onCloseModal,
      onColorModeChange,
      config,
    }
  },
})
